/**
 * Map unified טיסה+מלון bookingDraft → PackagePaymentView order + financial.
 */

import {
  buildUnifiedBaggageDetailItems,
  buildUnifiedBaggageDetailLabel,
} from "./buildUnifiedBaggageDetailLabel";
import { resolveUnifiedFlightLegs } from "./unifiedFlightLegs";

function formatDdMmYyyy(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
}

function totalIlsFromDraft(bookingDraft, currencyRateUsd) {
  const settled = Number(bookingDraft?.settledChargeIls);
  if (Number.isFinite(settled) && settled > 0) return settled;
  const totalFx = Number(bookingDraft?.totalFx);
  if (!Number.isFinite(totalFx) || totalFx <= 0) return null;
  const cur = String(bookingDraft?.currency || "USD").toUpperCase();
  const rate = Number(currencyRateUsd);
  if (cur === "ILS") return Math.round(totalFx);
  if (Number.isFinite(rate) && rate > 0) return Math.round(totalFx * rate);
  return Math.round(totalFx);
}

/**
 * @param {object} bookingDraft
 * @param {object} [appliedSearch]
 * @param {{ USD?: number }|null} [currencyRate]
 */
export function buildUnifiedFlightHotelPaymentVm(
  bookingDraft,
  appliedSearch = null,
  currencyRate = null
) {
  const rooms = Array.isArray(appliedSearch?.rooms) ? appliedSearch.rooms : [];
  const pax =
    Number(bookingDraft?.paxCount) ||
    (rooms.length
      ? rooms.reduce(
          (s, r) =>
            s +
            (Number(r.adults) || 0) +
            (Array.isArray(r.childAges) ? r.childAges.length : 0),
          0
        )
      : 2);

  const checkIn = formatDdMmYyyy(
    appliedSearch?.checkIn || bookingDraft?.checkIn
  );
  const checkOut = formatDdMmYyyy(
    appliedSearch?.checkOut || bookingDraft?.checkOut
  );

  const totalIls = totalIlsFromDraft(bookingDraft, currencyRate?.USD);
  const financial = {
    totalAmount: totalIls,
    paidAmount: 0,
    heldAmount: 0,
    remainingBalance: totalIls,
    isPartialPaid: false,
    isFullyPaid: false,
    isUnpaid: true,
  };

  const passengers = Array.isArray(bookingDraft?.passengers)
    ? bookingDraft.passengers.map((p) => ({
        name: [p.firstName, p.lastName].filter(Boolean).join(" ").trim(),
      }))
    : [];

  const leadName = [
    bookingDraft?.hubwayzLead?.name?.first,
    bookingDraft?.hubwayzLead?.name?.last,
  ]
    .filter(Boolean)
    .join(" ");

  const { outbound, inbound } = resolveUnifiedFlightLegs({
    outbound: bookingDraft?.outbound,
    inbound: bookingDraft?.inbound,
    rawDeal: bookingDraft?.rawDeal,
  });

  const order = {
    id: bookingDraft?.packageId || "usm-fh",
    orderNumber: bookingDraft?.prebook?.token
      ? `HOLD-${String(bookingDraft.prebook.token).slice(0, 8)}`
      : "טיוטה",
    dealType: "FlightsHotel",
    hotelName: bookingDraft?.hotelName || "טיסה + מלון",
    city: bookingDraft?.city || "",
    country: bookingDraft?.country || "",
    address: bookingDraft?.address || "",
    imageUrl: bookingDraft?.imageUrl || "",
    checkIn: checkIn || "—",
    checkOut: checkOut || "—",
    nights: bookingDraft?.nights ?? null,
    roomType: bookingDraft?.roomName || "—",
    roomsCount: rooms.length || 1,
    passengersCount: pax,
    boardLabel: bookingDraft?.boardLabel || "",
    agentName: "",
    clientName: leadName || bookingDraft?.clientName || "",
    passengers,
    currency: "ILS",
    flightLabel:
      bookingDraft?.flightOptionLabel || bookingDraft?.flightLabel || "",
    flight: {
      outbound: outbound || null,
      inbound: inbound || null,
    },
    baggageItems: buildUnifiedBaggageDetailItems({
      extras: bookingDraft?.extras,
      rawDeal: bookingDraft?.rawDeal,
    }),
    baggageLabel: buildUnifiedBaggageDetailLabel({
      extras: bookingDraft?.extras,
      rawDeal: bookingDraft?.rawDeal,
      baggageLabel: bookingDraft?.baggageLabel,
    }),
    financial,
  };

  return { order, financial };
}
